import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FieldInput } from "@/components/FieldInput";
import { ENTRY_KINDS } from "@/lib/entries";
import { api, errMsg, inr, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";

const COLORS = { vi: "#E60000", dse: "#D97706", fl: "#0284C7", cash: "#059669" };

function initial(cfg) {
  const v = { transaction_date: todayISO(), transaction_time: format(new Date(), "HH:mm") };
  cfg.fields.forEach((f) => { if (f.default) v[f.name] = f.default; });
  return v;
}

function PartyState({ kind, values }) {
  const [st, setSt] = useState(null);
  const rtl = values.retailer_id && kind !== "vtop-transfer";
  const pid = rtl ? values.retailer_id : values.dse_id || values.fl_id;
  const type = rtl ? "retailer" : values.dse_id ? "dse" : "fl";
  const did = values.distributor_id || "";
  useEffect(() => {
    setSt(null);
    if (!pid) return;
    api.get(`/masters/${type}/${pid}/state`, { params: { distributor_id: did } }).then((r) => setSt(r.data)).catch(() => {});
  }, [pid, type, did]);
  if (!st) return null;
  const total = (Number(values.cash_hisab) || 0) + (Number(values.upi_hisab) || 0);
  const after = Math.round(((st.wallet || 0) - total) * 100) / 100;
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs flex flex-wrap gap-x-5 gap-y-1" data-testid="entry-party-state">
      {"outstanding" in st && <span>Current outstanding: <b className="num">{inr(st.outstanding)}</b></span>}
      {"vtop_balance" in st && <span>Available VTOP balance: <b className="num" data-testid="entry-vtop-balance">{inr(st.vtop_balance)}</b></span>}
      {"vtop_due" in st && <span>Payment due: <b className="num" data-testid="entry-vtop-due">{inr(st.vtop_due)}</b></span>}
      {kind === "vtop-recharge" && Number(values.amount) > 0 && (
        <span className={Number(values.amount) > st.vtop_balance ? "text-rose-600" : "text-emerald-700"} data-testid="entry-vtop-after">
          Balance after recharge: <b className="num">{inr(Math.round((st.vtop_balance - Number(values.amount)) * 100) / 100)}</b>
        </span>
      )}
      {"wallet" in st && <span>Wallet balance ({did}): <b className="num" data-testid="entry-wallet-balance">{inr(st.wallet)}</b></span>}
      {kind === "dse-hisab" && (
        <div className="w-full grid grid-cols-2 gap-1 pt-2 mt-1 border-t border-slate-200 text-sm" data-testid="entry-hisab-calc">
          <span>Wallet Balance Before</span><b className="num text-right">{inr(st.wallet)}</b>
          <span>Total Hisab (Cash + UPI/Bank)</span><b className="num text-right">{inr(total)}</b>
          <span>Remaining Wallet After</span>
          <b className={`num text-right ${after < 0 ? "text-rose-600" : "text-emerald-700"}`} data-testid="entry-hisab-remaining">{inr(after)}</b>
          {after < 0 && <span className="col-span-2 text-rose-600" data-testid="entry-hisab-error">Excess ₹{Math.abs(after).toFixed(2)} — wallet balance se zyada hisab save nahi hoga. Pehle missing receipt/correction entry karein.</span>}
        </div>
      )}
      {"pending_advance" in st && <span>Pending advance: <b className="num">{inr(st.pending_advance)}</b></span>}
      {"commission_payable" in st && <span>Commission payable: <b className="num">{inr(st.commission_payable)}</b></span>}
    </div>
  );
}

export function EntryDialog({ kind, open, onOpenChange }) {
  const cfg = ENTRY_KINDS[kind];
  const masters = useMasters();
  const { bump } = useAuth();
  const [values, setValues] = useState(() => initial(cfg));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setValues(initial(cfg)); }, [open, cfg]);

  const set = (name) => (val) => {
    setValues((v) => {
      const nv = { ...v, [name]: val };
      if (name === "payment_mode" || name === "transaction_type") delete nv.fund_account_id;
      if (name === "scheme") {
        const s = masters.schemes.find((x) => x.name === val);
        if (s) nv.rate = String(s.rate);
      }
      if (name === "fl_id" && kind === "fl-commission" && !v.rate) {
        const m = masters.fl.find((x) => x.fl_id === val);
        if (m?.default_rate) nv.rate = String(m.default_rate);
      }
      return nv;
    });
  };

  const gross = useMemo(() => (Number(values.units) || 0) * (Number(values.rate) || 0), [values.units, values.rate]);
  const r2 = (x) => Math.round(x * 100) / 100;
  const amt = Number(values.amount) || 0;
  let calc = null;
  if (kind === "vi" && values.transaction_type === "Credit" && masters.rates.vi >= 0) {
    const ex = r2((amt * masters.rates.vi) / 100);
    calc = [["VI extra rate", `${masters.rates.vi}%`], ["VI extra amount", inr(ex)], ["VI virtual credit", inr(amt + ex)]];
  }
  if (kind === "dse-transfer" && masters.rates.dse >= 0) {
    const rt = masters.rates.dse;
    const base = values.amount_type === "base" ? amt : r2(amt / (1 + rt / 100));
    const total = values.amount_type === "base" ? r2(amt + (amt * rt) / 100) : amt;
    calc = [["DSE extra rate", `${rt}%`], ["DSE base (outstanding)", inr(base)], ["DSE extra", inr(r2(total - base))], ["Total balance given (VI debit)", inr(total)]];
  }
  if (kind === "vtop-transfer" && masters.rates.retailer >= 0) {
    const mg = r2((amt * masters.rates.retailer) / 100);
    calc = [["Margin rate", `${masters.rates.retailer}%`], ["Margin (P&L)", inr(mg)], ["Retailer VTOP credit", inr(amt + mg)], ["DSE hisab debit", inr(amt)]];
  }
  const visible = cfg.fields.filter((f) => !f.showIf || f.showIf(values))
    .map((f) => ({ ...f, required: typeof f.required === "function" ? f.required(values) : !!f.required }));

  const submit = async (e) => {
    e.preventDefault();
    const missing = visible.find((f) => f.required && !values[f.name]);
    if (missing) return toast.error(`${missing.label} is required`);
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(values).filter(([k]) => visible.some((f) => f.name === k)));
      const { data } = await api.post(`/entries/${kind}`, payload);
      toast.success(`Saved: ${data.transaction_ids.join(", ")}`);
      bump();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const tp = `entry-${kind}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`${tp}-dialog`}>
        <DialogHeader>
          <div className="h-1 w-12 rounded-full mb-2" style={{ background: COLORS[cfg.ledger] }} />
          <DialogTitle className="font-display text-xl">{cfg.title}</DialogTitle>
          <DialogDescription className="hint-tag w-fit">{cfg.hint}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3.5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
            {visible.map((f) => (
              <div key={f.name} className={f.type === "textarea" || f.hint ? "sm:col-span-2" : ""}>
                <FieldInput f={f} value={values[f.name]} onChange={set(f.name)} masters={masters} values={values} testPrefix={tp} />
              </div>
            ))}
          </div>
          {kind === "fl-commission" && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm grid grid-cols-2 gap-2" data-testid="entry-commission-calc">
              <span>Gross commission</span><b className="num text-right">{inr(gross)}</b>
              <span>Net payable</span><b className="num text-right">{inr(gross - (Number(values.advance_adjusted) || 0))}</b>
            </div>
          )}
          {calc && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm grid grid-cols-2 gap-1.5" data-testid="entry-extra-calc">
              {calc.flatMap(([l, x]) => [<span key={`${l}-l`}>{l}</span>, <b key={`${l}-v`} className="num text-right">{x}</b>])}
            </div>
          )}
          {(values.dse_id || values.fl_id || values.retailer_id) && <PartyState kind={kind} values={values} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid={`${tp}-cancel`}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid={`${tp}-submit`} style={{ background: COLORS[cfg.ledger] }}>
              {saving ? "Saving..." : "Save Entry"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
